/**
 * Inline "save this price to the client catalog" button for an order line.
 *
 * Shows next to a line-item price only for sales admins, and only when the
 * entered price differs from the client's current catalog price; clicking it
 * upserts the new price into the customer catalog so future orders for that
 * client default to it.
 *
 * Contains:
 * - SavePriceButton — sales-admin-only catalog price-save action button.
 */
import { BookmarkBorderOutlined } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { patchCustomerCatalog } from '@sophocles/api/customerCatalog';
import { useUser } from '@sophocles/app';
import { MONEY_FORMAT_OPTIONS } from '@sophocles/data-grids';
import { addToast } from '@sophocles/features/toast';
import { formatNumber } from '@sophocles/utils/formatUtils';

function SavePriceButton({ item, liveCustomerId, customerCatalog, isSalesAdmin, products, onSaved }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  // Resolve display values unconditionally (hooks must not appear after early returns).
  const product = products.find(p => p.id === item.item) ?? null;
  const catalogEntry = product ? customerCatalog.find(cp => cp.product?.id === item.item) : null;
  const currentCatalogPrice = catalogEntry?.price ?? product?.item_price;
  const formPrice = Number(item.item_price);
  const catalogPrice = Number(currentCatalogPrice);
  const priceDiffers = Number.isFinite(formPrice) && Math.abs(formPrice - catalogPrice) >= 0.001;

  const handleSave = useCallback(async () => {
    if (!product) return;
    setSaving(true);
    try {
      const updated = await patchCustomerCatalog(liveCustomerId, [
        {
          customer_id: liveCustomerId,
          item_part_num: product.item_part_num,
          action: 'upsert',
          price: formPrice,
        },
      ]);
      onSaved(updated);
      dispatch(addToast({ message: t('warehouse.catalogPriceSaved', 'Price saved for client'), level: 'success' }));
    } catch {
      dispatch(addToast({ message: t('warehouse.catalogPriceSaveFailed', 'Failed to save price'), level: 'error' }));
    } finally {
      setSaving(false);
    }
  }, [product, liveCustomerId, formPrice, onSaved, dispatch, t]);

  if (!isSalesAdmin || !liveCustomerId || !item.item || !product || !priceDiffers) return null;

  return (
    <Tooltip
      title={t('warehouse.savePriceForClient', 'Save ${{price}} for this client', {
        price: formatNumber(formPrice, user, MONEY_FORMAT_OPTIONS),
      })}
    >
      <span>
        <IconButton
          size="small"
          onClick={handleSave}
          disabled={saving}
          color="primary"
          aria-label={t('warehouse.savePriceForClient', 'Save ${{price}} for this client', {
            price: formatNumber(formPrice, user, MONEY_FORMAT_OPTIONS),
          })}
          sx={{ flexShrink: 0 }}
        >
          <BookmarkBorderOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default SavePriceButton;
